import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import * as Sentry from '@sentry/react';
import ModalContainer from '../../containers/ModalContainer';
import Tabs from '../../../display/Tabs';
import { InputRadioTagTable } from '../../../display/tables/InputRadioTagTable';
import { tableProps as InputRadioTagTableProps } from '../../../display/tables/InputRadioTagTable/types';
import { tableIds, takeModal } from '../../../display/tables/types';
import { GET_GENERIC_ORDERS_MODAL } from '../../../../gql/queries/genericOrders';
import { GET_CAMPAIGNS_MODAL } from '../../../../gql/queries/campaign';
import {
  GET_CONCESSIONS,
  GET_ORDERS_FOR_ROUTE_LINK,
} from '../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { useAuth } from '../../../../hooks/useAuth';
import { useFreshTableCursor } from '../../../../hooks/useFreshTableCursor';
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { formatPK } from '../../../../utils/surveillanceRoute';
import { closeModal } from '../../../../state/actions/ui/modal';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { useOnlineStatus } from '../../../../offline/useOnlineStatus';
import { OrderState, OrderType } from '../../../../types/orders';
import { Role } from '../../../../types/auth';
import { closedNotBeforeFor } from '../../../../utils/avisoCloseWindow';
import { getValidStatesForEntityType } from '../../../../constants/entityStateFiltering';
import { RelateOrderToRouteModalData, RelateOrderToRouteModalProps } from './types';
import {
  Container,
  ButtonContainer,
  MainButtonStyled,
  CloseButton,
  EmptyMessage,
  PaginationButtonsStyled,
} from '../RelateSegmentModal/styles';

/** Columnas que no aportan para elegir una OT que vincular. */
const HIDDEN_COLUMN_IDS = [
  'capitol',
  'subCapitol',
  'predefinedOrder',
  'concession',
  'team',
  'road',
  'track',
  'direction',
  'user',
  'creationDate',
];

type LinkTarget = NonNullable<RelateOrderToRouteModalData['target']>;

/**
 * Lo que no se ofrece al vincular (QA 15-oct, T3.3+a — se listaban las
 * anuladas con la casilla activa y el tramo quedaba «Vinculado» con una OT
 * anulada). Ninguna anulada, ni al recorrido ni a un aviso. Las cerradas SÍ
 * se ofrecen a un aviso (spec 2026-11-02): el servidor solo manda las
 * cerradas a partir de `closedNotBefore`; a la tablet se le quitan aparte
 * (`linkableStatesOf`).
 */
const EXCLUDED_STATES: Record<LinkTarget, readonly string[]> = {
  route: [OrderState.Annulled],
  aviso: [OrderState.Annulled],
};

/**
 * Estados que SÍ se pueden vincular, ordenados (así la clave de caché de
 * Apollo es estable). Se piden al servidor en vez de quitar filas después:
 * con la lista paginada en el servidor, quitar filas en cliente dejaría
 * páginas cortas y un total que no cuadra.
 */
export const linkableStatesOf = (
  target: LinkTarget,
  role: string | null | undefined,
): string[] =>
  [
    ...new Set<string>([
      ...getValidStatesForEntityType(OrderType.Generic),
      ...getValidStatesForEntityType(OrderType.Campaign),
    ]),
  ]
    .filter((state) => !EXCLUDED_STATES[target].includes(state))
    // La tablet no ve cerradas al vincular un aviso (spec § 4.4, decisión 6).
    .filter(
      (state) =>
        !(target === 'aviso' && role === Role.Tablet && state === OrderState.End),
    )
    .sort();

/**
 * Una pestaña por listado (2026-10-28), como «Transferir» (TransferSubOrderModal):
 * el back no tiene un listado que junte genéricas y campañas, y paginar por
 * cursor dos listas mezcladas no se puede. `sortFields` es la whitelist de
 * ordenación de cada back (GENERIC_ORDER_SORTABLE_FIELDS /
 * CAMPAIGN_SORTABLE_FIELDS) recortada a las columnas que se ven aquí; un campo
 * fuera de ella el back lo rechaza.
 */
type ListKind = 'generic' | 'campaign';
const LIST_SOURCES = {
  generic: {
    query: GET_GENERIC_ORDERS_MODAL,
    field: 'genericOrders',
    sortFields: new Set(['state', 'registerInitDate', 'pkInitKm', 'pkEndKm']),
  },
  campaign: {
    query: GET_CAMPAIGNS_MODAL,
    field: 'campaigns',
    sortFields: new Set(['state', 'registerInitDate', 'pkInitKm', 'pkEndKm', 'name']),
  },
} as const;

/**
 * Dónde está cada pestaña. El cursor es el id de la ENTIDAD (GenericOrder o
 * Campaign) de la última fila (hacia delante, `take` > 0) o de la primera
 * (hacia atrás, `take` < 0), igual que en el resto de tablas.
 */
type PageCursor = { cursor: string; take: number; page: number };
const FIRST_PAGE: PageCursor = { cursor: '', take: takeModal, page: 1 };
const FIRST_PAGES: Record<ListKind, PageCursor> = {
  generic: FIRST_PAGE,
  campaign: FIRST_PAGE,
};

/** Filtros de la barra, tal cual los escribe la tabla en su cursor al pulsar la lupa. */
type AppliedFilters = Record<string, unknown>;

/**
 * Los filtros de Órdenes de trabajo (y de Duplicar O.T.), con los mismos
 * nombres que acepta el back en `FilterGenericList` y `FilterCampaignList`.
 * Una lista vacía o un campo sin valor no se manda. El Estado va aparte
 * (`statesToRequest`): siempre viaja, recortado a lo vinculable.
 */
const FILTER_FIELDS = [
  'indexExpedition',
  'indexType',
  'description',
  'fromDate',
  'toDate',
  'concessionId',
  'teamIds',
  'classification',
  'userIds',
  'capitolId',
  'subCapitolId',
  'predefinedOrderId',
  'pkInitKm',
  'pkInitMeter',
  'pkEndKm',
  'pkEndMeter',
  'track',
] as const;

const NUMERIC_FIELDS = new Set<string>([
  'indexExpedition',
  'indexType',
  'pkInitKm',
  'pkInitMeter',
  'pkEndKm',
  'pkEndMeter',
]);

const sortDirectionOf = (applied: AppliedFilters): 'asc' | 'desc' =>
  applied.orderBy === 'asc' ? 'asc' : 'desc';

/** Lo elegido en el filtro Estado (si hay algo) dentro de lo vinculable. */
function statesToRequest(applied: AppliedFilters, linkable: string[]): string[] {
  const chosen = Array.isArray(applied.states) ? applied.states : [];
  return chosen.length === 0
    ? linkable
    : linkable.filter((state) => chosen.includes(state));
}

function toListInput(
  applied: AppliedFilters,
  kind: ListKind,
  page: PageCursor,
  states: string[],
  closedNotBefore?: string,
): Record<string, unknown> {
  const input: Record<string, unknown> = {
    take: page.take,
    cursor: page.cursor,
    orderBy: 'desc',
    states,
  };
  // Con cerradas en la lista (solo al vincular un aviso), el servidor deja
  // pasar únicamente las cerradas a partir de esta hora.
  if (closedNotBefore && states.includes(OrderState.End)) {
    input.closedNotBefore = closedNotBefore;
  }
  for (const field of FILTER_FIELDS) {
    const value = applied[field];
    if (value == null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    input[field] = NUMERIC_FIELDS.has(field) ? Number(value) : value;
  }
  const sortField = applied.sortField;
  if (typeof sortField === 'string' && LIST_SOURCES[kind].sortFields.has(sortField)) {
    input.sortField = sortField;
    input.orderBy = sortDirectionOf(applied);
  }
  return input;
}

type ListEntity = { id: string; orderIDs?: string[] | null; name?: string | null };

type OrderRow = {
  id: string;
  roid: string;
  state: string;
  type: string;
  description?: string;
  createdAt?: string;
  registerInitDate?: string | null;
  derivedEndDate?: string | null;
  pkInitKm?: number | null;
  pkInitMeter?: number | null;
  pkEndKm?: number | null;
  pkEndMeter?: number | null;
};

/** Una fila: la Order resuelta y la entidad del listado de la que sale. */
type LinkRow = OrderRow & { entityId: string; entityName: string };

type ListPage = {
  rows: LinkRow[];
  /** La página TAL CUAL la mandó el servidor: de aquí sale el cursor. */
  entities: ListEntity[];
  totalCount: number;
  hasNextPage: boolean;
  loading: boolean;
  error: unknown;
};

/**
 * Una página de un listado (genéricas o campañas) con sus Orders resueltas.
 * Las entidades no llevan ROID: se resuelven por id (`GET_ORDERS_FOR_ROUTE_LINK`),
 * en el orden del listado, porque `ordersByIds` no garantiza ninguno.
 *
 * `input` a `null` = no hay nada que pedir (el filtro Estado solo tenía
 * estados que no se pueden vincular): lista vacía, sin consulta.
 */
function useListPage(
  kind: ListKind,
  input: Record<string, unknown> | null,
  active: boolean,
  excludedOrderIds: ReadonlySet<string>,
): ListPage {
  const apolloClient = useApolloClient();
  const source = LIST_SOURCES[kind];
  const skip = !active || input === null;
  const { data, previousData, loading, error } = useQuery(source.query, {
    variables: { input },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const list = data?.[source.field];
  // Total y «hay más» de la última respuesta mientras llega la siguiente: si
  // no, el paginador desaparece y vuelve a salir en cada cambio de página.
  const lastList = (data ?? previousData)?.[source.field];
  const entities: ListEntity[] = useMemo(
    () => (input === null ? [] : (list?.[source.field] ?? [])),
    [input, list, source.field],
  );

  // Las ya vinculadas al recorrido no se ofrecen otra vez. Se quitan aquí
  // porque el back no sabe excluir ids: la página puede salir con alguna
  // fila menos, pero el cursor sigue saliendo de `entities`, la página
  // completa, y no se salta ni se repite nada.
  const orderIds = useMemo(
    () =>
      entities
        .map((entity) => entity.orderIDs?.[0])
        .filter((id): id is string => Boolean(id) && !excludedOrderIds.has(id as string)),
    [entities, excludedOrderIds],
  );
  const idsKey = orderIds.join(',');
  const listReady = input === null || data !== undefined || Boolean(error);

  // Para qué ids se resolvieron las filas que hay. Mientras no coincide, la
  // lista está a medio llegar y cuenta como cargando: así el cargando sale una
  // sola vez, de la consulta a las filas (feedback usuario 2026-10-27).
  const [resolved, setResolved] = useState<{ key: string | null; rows: LinkRow[] }>({
    key: null,
    rows: [],
  });

  // La página que toca resolver, leída por ref: el efecto depende solo de la
  // CLAVE de ids. Cuando la red confirma lo que vino de la caché llegan
  // objetos nuevos con los mismos ids, y con ellos en las dependencias se
  // cancelaba la resolución en curso y se lanzaba otra igual.
  const pageRef = useRef({ entities, orderIds });
  pageRef.current = { entities, orderIds };

  useEffect(() => {
    if (!active || !listReady || idsKey === resolved.key) return undefined;
    let cancelled = false;
    const { entities: pageEntities, orderIds: ids } = pageRef.current;
    const entityByOrderId = new Map(
      pageEntities.map((entity) => [entity.orderIDs?.[0], entity]),
    );
    const resolve = async () => {
      if (ids.length === 0) {
        setResolved({ key: idsKey, rows: [] });
        return;
      }
      try {
        const { data: ordersData } = await apolloClient.query({
          query: GET_ORDERS_FOR_ROUTE_LINK,
          variables: { ids },
          fetchPolicy: 'cache-first',
        });
        if (cancelled) return;
        const orderById = new Map<string, OrderRow>(
          (ordersData?.ordersByIds ?? []).map((order: OrderRow) => [order.id, order]),
        );
        const rows = ids
          .map((id) => orderById.get(id))
          .filter(
            (order): order is OrderRow =>
              order?.type === OrderType.Generic || order?.type === OrderType.Campaign,
          )
          .map((order) => {
            const entity = entityByOrderId.get(order.id);
            return { ...order, entityId: entity?.id ?? '', entityName: entity?.name ?? '' };
          });
        setResolved({ key: idsKey, rows });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: 'surveillance-route', component: 'RelateOrderToRouteModal' },
        });
        // Sin esto el cargando no se apagaría nunca. Quedan las filas de antes.
        if (!cancelled) setResolved((prev) => ({ key: idsKey, rows: prev.rows }));
      }
    };
    resolve().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [active, listReady, idsKey, resolved.key, apolloClient]);

  return {
    rows: resolved.rows,
    entities,
    totalCount: input === null ? 0 : (lastList?.totalCount ?? 0),
    hasNextPage: Boolean(lastList?.pageInfo?.hasNextPage),
    loading: active && (loading || resolved.key !== idsKey || !listReady),
    error,
  };
}

/** PK de la fila en formato de pantalla, o vacío si la OT no lo tiene. */
const pkOf = (km?: number | null, meter?: number | null): string =>
  km != null && meter != null ? formatPK(km, meter) : '';

/**
 * «Vincular OT» desde el detalle del recorrido (cross-link 3, 2026-09-22) o
 * desde el aviso de un tramo. Reusa `InputRadioTagTable` (routing-agnóstica,
 * vive bien en un modal) en dos pestañas, OTs genéricas y campañas, paginadas
 * en el servidor de 8 en 8 (2026-10-28; antes las 50 más recientes de cada
 * una, paginadas en cliente: una OT más antigua no se alcanzaba).
 */
export const RelateOrderToRouteModal = ({
  onClickClose,
  data,
}: RelateOrderToRouteModalProps): JSX.Element => {
  const { t } = useTranslation(['modals', 'common', 'tables']);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isOnline } = useOnlineStatus();
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const target: LinkTarget = data.target ?? 'route';

  // Filtros y orden vacíos al abrir, ANTES de pintar la tabla (2026-10-26).
  // Antes era `setCursor(id, {})`, que no borraba nada porque el reducer
  // mezcla: el modal reabría con el expediente de la vez anterior.
  const isCursorFresh = useFreshTableCursor(tableIds.ORDER_LINK_MODAL);

  // Filtros de la barra (los mismos que Órdenes de trabajo). Como en el resto
  // de tablas, se aplican al pulsar la lupa, la papelera o una cabecera: la
  // tabla escribe su cursor y llama a `onReFetchData`, y es entonces cuando se
  // lee. Son los mismos para las dos pestañas: se busca «la OT del PK 234» y
  // se mira en las dos.
  const [applied, setApplied] = useState<AppliedFilters>({});
  const [pages, setPages] = useState(FIRST_PAGES);
  const applyFilters = useCallback(() => {
    setApplied({ ...getCursor(tableIds.ORDER_LINK_MODAL)(store.getState()) });
    // Resultados nuevos u otro orden: las dos pestañas a su primera página.
    setPages(FIRST_PAGES);
  }, []);

  const [tabIndex, setTabIndex] = useState(0);
  const activeKind: ListKind = tabIndex === 1 ? 'campaign' : 'generic';

  const { currentRole } = useAuth();
  const linkableStates = useMemo(
    () => linkableStatesOf(target, currentRole),
    [target, currentRole],
  );
  // 🔴 Una sola vez al abrir la ventana: con `new Date()` en cada render la
  // clave de Apollo cambiaría siempre y la lista recargaría sin parar.
  const [closedNotBefore] = useState(() =>
    target === 'aviso' ? closedNotBeforeFor(data.avisoAt, new Date()) : undefined,
  );
  const states = useMemo(
    () => statesToRequest(applied, linkableStates),
    [applied, linkableStates],
  );
  const excludedOrderIds = useMemo(
    () => new Set(data.excludeOrderIds ?? []),
    [data.excludeOrderIds],
  );
  // Sin ningún estado que pedir (el filtro solo tenía estados que no se
  // pueden vincular) no hay consulta: la lista sale vacía.
  const genericInput = useMemo(
    () => (states.length === 0 ? null : toListInput(applied, 'generic', pages.generic, states, closedNotBefore)),
    [applied, pages.generic, states, closedNotBefore],
  );
  const campaignInput = useMemo(
    () => (states.length === 0 ? null : toListInput(applied, 'campaign', pages.campaign, states, closedNotBefore)),
    [applied, pages.campaign, states, closedNotBefore],
  );

  // Solo se pide la pestaña que se ve; la otra, al entrar en ella.
  const genericPage = useListPage('generic', genericInput, activeKind === 'generic', excludedOrderIds);
  const campaignPage = useListPage('campaign', campaignInput, activeKind === 'campaign', excludedOrderIds);
  const current = activeKind === 'generic' ? genericPage : campaignPage;
  const currentPage = pages[activeKind];
  const { loading, error } = current;

  // Opciones de los desplegables: los mismos catálogos que carga Duplicar O.T.
  const { data: capitolsData } = useQuery(FETCH_CAPITOLS);
  const { data: concessionsData } = useQuery(GET_CONCESSIONS);
  const { data: subCapitolsData } = useQuery(FETCH_SUB_CAPITOLS);
  const { data: classificationsData } = useQuery(FETCH_CLASSIFICATIONS);
  const { data: teamsData } = useQuery(FETCH_TEAMS);
  const { data: predefinedData } = useQuery(FETCH_PREDEFINED_ORDERS);
  const { data: usersData } = useQuery(FETCH_USERS);

  const adaptedRows = useMemo(
    () =>
      current.rows.map((o) => ({
        id: o.id,
        roid: o.roid,
        // Columna «Nombre», solo en la pestaña de campañas.
        name: o.entityName,
        state: o.state,
        specialTag: o.type,
        genericOrCampaignOrderID: o.entityId,
        creationDate: o.createdAt ?? '',
        // La fecha que se ve es la de REGISTRO, la misma por la que filtra
        // «Fecha inicio» y ordena la cabecera (antes salía la de creación).
        date: o.registerInitDate ?? o.createdAt ?? '',
        registerInitDate: o.registerInitDate ?? o.createdAt ?? '',
        // La misma que el listado de Órdenes de trabajo: solo la tiene una OT
        // cerrada (antes iba siempre vacía).
        derivedEndDate: o.derivedEndDate ?? '',
        description: o.description ?? '',
        pkInit: pkOf(o.pkInitKm, o.pkInitMeter),
        pkEnd: pkOf(o.pkEndKm, o.pkEndMeter),
        concession: '',
        road: '',
        capitol: '',
        subCapitol: '',
        user: '',
        userName: '',
      })),
    [current.rows],
  );

  // Lo que se puede elegir es lo que se ve: si un filtro o el cambio de página
  // quitan la OT elegida, la tabla suelta la selección y «Vincular» se
  // desactiva. Solo con la página ya resuelta para los ids actuales: con la
  // lista a medias la soltaba aunque el filtro la mantuviera.
  const selectableRowIds = useMemo(
    () => adaptedRows.map((row) => row.id),
    [adaptedRows],
  );

  // Solo la OT cerrada tiene fecha final: si el rol no puede vincular
  // cerradas (la tablet con un aviso), la columna saldría siempre vacía.
  const hiddenColumnIds = useMemo(
    () =>
      linkableStates.includes(OrderState.End)
        ? HIDDEN_COLUMN_IDS
        : [...HIDDEN_COLUMN_IDS, 'derivedEndDate'],
    [linkableStates],
  );

  // Cargando canónico de las tablas (feedback usuario 2026-09-24): mientras
  // llega la página y mientras se vincula, la tabla pone su Loader encima y se
  // atenúa. Las tablas compartidas solo lo leen de aquí, no de `data.loading`.
  useEffect(() => {
    dispatch(loadingData(tableIds.ORDER_LINK_MODAL, loading || isSubmitting));
  }, [dispatch, loading, isSubmitting]);
  useEffect(
    () => () => {
      dispatch(loadingData(tableIds.ORDER_LINK_MODAL, false));
    },
    [dispatch],
  );

  const pageCount = Math.max(1, Math.ceil(current.totalCount / takeModal));
  const goToPage = useCallback((kind: ListKind, next: PageCursor) => {
    setPages((prev) => ({ ...prev, [kind]: next }));
  }, []);
  // Cursor de la página que mandó el servidor (`entities`), no de las filas
  // que se ven: sin las ya vinculadas, la siguiente se saltaría o repetiría.
  const nextPage = () => {
    const last = current.entities[current.entities.length - 1];
    if (loading || !last || !current.hasNextPage || currentPage.page >= pageCount) return;
    goToPage(activeKind, { cursor: last.id, take: takeModal, page: currentPage.page + 1 });
  };
  const previousPage = () => {
    if (loading || currentPage.page <= 1) return;
    const first = current.entities[0];
    // A la primera, por su camino (cursor vacío), como el resto de tablas.
    if (currentPage.page <= 2 || !first) {
      goToPage(activeKind, FIRST_PAGE);
      return;
    }
    goToPage(activeKind, { cursor: first.id, take: -takeModal, page: currentPage.page - 1 });
  };

  const handleConfirm = useCallback(
    async (orderId?: string) => {
      const id = orderId ?? selectedOrderId;
      if (!id) return;
      try {
        setIsSubmitting(true);
        await data.onConfirm(id);
        dispatch(closeModal());
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: 'surveillance-route', component: 'RelateOrderToRouteModal' },
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, dispatch, selectedOrderId],
  );

  const tableData = useMemo(
    () => ({
      genericOrders: adaptedRows,
      filtersDataOptions: {
        concessions: concessionsData?.concessions ?? [],
        capitols: capitolsData?.capitols ?? [],
        subCapitols: subCapitolsData?.subCapitols ?? [],
        classifications: classificationsData?.classifications ?? [],
        teams: teamsData?.teams ?? [],
        predefinedOrders: predefinedData?.predefinedOrders ?? [],
        users: usersData?.users ?? [],
      },
      totalCount: current.totalCount,
      pageInfo: { hasNextPage: current.hasNextPage, hasPreviousPage: currentPage.page > 1 },
      loading,
      roleActive: currentRole,
    }),
    [
      adaptedRows,
      current.totalCount,
      current.hasNextPage,
      currentPage.page,
      loading,
      concessionsData,
      capitolsData,
      subCapitolsData,
      classificationsData,
      teamsData,
      predefinedData,
      usersData,
      currentRole,
    ],
  );

  const tableOf = (kind: ListKind) => (
    <InputRadioTagTable
      titleText={
        target === 'aviso'
          ? t('modals:relateOrderToRoute.titleAviso')
          : t('modals:relateOrderToRoute.title')
      }
      data={tableData as unknown as InputRadioTagTableProps['data']}
      setRowState={setSelectedOrderId}
      onReFetchData={applyFilters}
      onReFetchPagination={() => undefined}
      tableId={tableIds.ORDER_LINK_MODAL}
      hiddenColumnIds={hiddenColumnIds}
      // En la pestaña de campañas, su nombre; en la de OTs no hay columna de
      // tipo (antes la había porque iban mezcladas): ya lo dice la pestaña.
      showRouteNameColumn={kind === 'campaign'}
      routeNameHeaderLabel={t('tables:headers.name')}
      showRouteNameFilter={false}
      showButtonsPagination={false}
      excludedStateOptions={EXCLUDED_STATES[target]}
      selectableRowIds={loading ? undefined : selectableRowIds}
      onRowDoubleTap={(row) => {
        if (row.original.id) {
          setSelectedOrderId(row.original.id);
          handleConfirm(row.original.id);
        }
      }}
    />
  );

  return (
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Container data-testid="relate-order-to-route-modal">
        {isCursorFresh && (
          <Tabs
            defaultIndex={0}
            typeTab={tabsType.RELATE_ORDER_TO_ROUTE}
            setSelectedIndexTab={setTabIndex}
            data={[
              { label: t('common:tabs.workOrders'), content: tableOf('generic') },
              { label: t('common:tabs.campaigns'), content: tableOf('campaign') },
            ]}
          />
        )}
        {!loading && Boolean(error) && (
          <EmptyMessage>
            {isOnline
              ? t('common:messages.error')
              : t('common:offline.noConnection')}
          </EmptyMessage>
        )}
        {!loading && !error && adaptedRows.length === 0 && (
          <EmptyMessage>
            {isOnline
              ? t('modals:relateOrderToRoute.empty')
              : t('common:offline.noConnection')}
          </EmptyMessage>
        )}
        <ButtonContainer>
          <CloseButton
            text={t('common:buttons.back')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          <PaginationButtonsStyled
            pageCount={pageCount}
            currentPage={currentPage.page}
            totalCount={current.totalCount}
            nextPage={nextPage}
            previousPage={previousPage}
            isDisabled={loading}
          />
          <MainButtonStyled
            text={t('modals:relateOrderToRoute.link')}
            onClick={() => handleConfirm()}
            disabled={!selectedOrderId || isSubmitting}
          />
        </ButtonContainer>
      </Container>
    </ModalContainer>
  );
};

export default RelateOrderToRouteModal;
